import React from 'react';
import { useDataFetching } from '../../hooks/useDataFetching';
import LoadingState from '../shared/LoadingState';
import ErrorState from '../shared/ErrorState';
import StatCard from '../shared/StatCard';
import { formatRelativeTime } from '../../utils/formatters';
import { Plus, Database, PlayCircle, PauseCircle } from 'lucide-react';

const DataPipeline = () => {
  const { data: pipelines, loading, error, refetch } = useDataFetching(
    async () => {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pipelines`);
      if (!response.ok) throw new Error('Failed to fetch pipelines');
      return response.json();
    },
    []
  );

  if (loading) return <LoadingState message="Loading data pipelines..." />;
  if (error) return <ErrorState message={error} onRetry={refetch} />;

  const runningPipelines = pipelines?.filter(p => p.status === 'running').length || 0;
  const avgSuccessRate = pipelines?.length
    ? (pipelines.reduce((sum, p) => sum + p.success_rate, 0) / pipelines.length).toFixed(1)
    : 0;
  const totalErrors = pipelines?.reduce((sum, p) => sum + p.error_count, 0) || 0;

  const getStatusColor = (status) => {
    if (status === 'running') return 'bg-green-100 text-green-800';
    if (status === 'paused') return 'bg-yellow-100 text-yellow-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Data Pipeline</h1>
          <p className="mt-2 text-gray-600">
            Monitor and manage data ingestion pipelines
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Pipelines"
            value={pipelines?.length || 0}
            subtitle="Configured"
          />
          <StatCard
            title="Running"
            value={runningPipelines}
            subtitle="Active pipelines"
          />
          <StatCard
            title="Avg Success Rate"
            value={`${avgSuccessRate}%`}
            subtitle="Overall health"
          />
          <StatCard
            title="Total Errors"
            value={totalErrors}
            subtitle="Last 24 hours"
          />
        </div>

        {/* Action Button */}
        <div className="mb-6 flex justify-end">
          <button
            data-testid="new-pipeline-btn"
            className="inline-flex items-center px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <Plus className="h-5 w-5 mr-2" />
            New Pipeline
          </button>
        </div>

        {/* Pipelines Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Active Pipelines</h2>
            <p className="text-sm text-gray-600 mt-1">Monitor data flow from sources to destinations</p>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pipeline Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source → Destination
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Throughput
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Success Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Run
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pipelines && pipelines.map((pipeline) => (
                  <tr key={pipeline.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <Database className="h-5 w-5 text-blue-500 mr-2" />
                        <div className="text-sm font-medium text-gray-900">{pipeline.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {pipeline.source} → {pipeline.destination}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(pipeline.status)}`}>
                        {pipeline.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {pipeline.throughput}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-sm text-gray-900">{pipeline.success_rate}%</div>
                        {pipeline.error_count > 0 && (
                          <span className="ml-2 text-xs text-red-600">({pipeline.error_count} errors)</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatRelativeTime(pipeline.last_run)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button className="text-blue-600 hover:text-blue-900 mr-3">
                        View
                      </button>
                      <button className="text-gray-600 hover:text-gray-900">
                        {pipeline.status === 'running' ? (
                          <PauseCircle className="h-5 w-5 inline" />
                        ) : (
                          <PlayCircle className="h-5 w-5 inline" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataPipeline;